import ProxyImage from "@/components/ProxyImage";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderStatus from "@/enum/OrderStatus";
import ShopType from "@/enum/ShopType";
import { showDeviceSnShopTypes, showEanCodeShopTypes, showIMEI2ShopTypes } from "@/utils/ShopUtils";
import { parseOrderItemExtraInfo } from "@/utils/OrderUtils";
import { Form, FormInstance, Input, Table } from "antd";
import type { TableColumnsType } from "antd";
import React, { useMemo } from "react";

interface IMEIFormTableProps {
    form: FormInstance<IMEIFormTableValues>;
    orders: OrderDTO[];
    shopType: ShopType;
}

export interface IMEIFormTableValues {
    [orderOriginalId: string]: {
        [orderItemOriginalId: string]: {
            imei?: string;
            imei2?: string;
            deviceSn?: string;
            eanCode?: string;
        };
    };
}

interface OrderItemRow extends OrderItemDTO {
    index: number;
    orderId: number;
    orderOriginalId: string;
    orderItemLength: number;
    orderItemId: number;
    imeis?: string[];
    imei2s?: string[];
    deviceSns?: string[];
    eanCodes?: string[];
    orderStatus: OrderStatus;
    isOrderItemRefund: boolean;
}

function buildOrderItemStatus(orderStatus: OrderStatus, orderItemStatus: OrderStatus, isOrderItemRefund: boolean) {
    if (orderStatus === orderItemStatus) {
        return null;
    }
    if (orderItemStatus === OrderStatus.SHIPPED) {
        return (
            <p className="text-success font-bold mt5">
                <small>已发货</small>
            </p>
        );
    }
    if (orderItemStatus === OrderStatus.CANCELED) {
        return (
            <p className="text-danger font-bold mt5">
                <small>已取消</small>
            </p>
        );
    }

    if (isOrderItemRefund) {
        return (
            <p className="text-danger font-bold mt5">
                <small>退款中</small>
            </p>
        );
    }

    if (orderItemStatus === OrderStatus.SUCCESS) {
        return (
            <p className="text-success font-bold mt5">
                <small>已完成</small>
            </p>
        );
    }
    return null;
}

const IMEIFormTable: React.FC<IMEIFormTableProps> = function (props) {
    const { form, orders, shopType } = props;

    const orderItemRows = useMemo(() => {
        return orders.reduce<OrderItemRow[]>((total, order) => {
            if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT) {
                order.childOrders.forEach((childOrder) => {
                    childOrder.orderItems.forEach((orderItem, index) => {
                        const orderItemExtraInfo = parseOrderItemExtraInfo(orderItem);

                        total.push({
                            ...orderItem,
                            index,
                            orderId: childOrder.id,
                            orderItemId: orderItem.id,
                            orderItemLength: childOrder.orderItems.length,
                            orderOriginalId: childOrder.originalId,
                            imeis: orderItemExtraInfo?.imeis,
                            imei2s: orderItemExtraInfo?.imei2s,
                            deviceSns: orderItemExtraInfo?.deviceSns,
                            eanCodes: orderItemExtraInfo?.eanCodes,
                            orderStatus: childOrder.status,
                            isOrderItemRefund: orderItem.isRefunding,
                        });
                    });
                });
            } else {
                order.orderItems.forEach((orderItem, index) => {
                    const orderItemExtraInfo = parseOrderItemExtraInfo(orderItem);

                    total.push({
                        ...orderItem,
                        index,
                        orderId: order.id,
                        orderItemId: orderItem.id,
                        orderItemLength: order.orderItems.length,
                        orderOriginalId: order.originalId,
                        imeis: orderItemExtraInfo?.imeis,
                        imei2s: orderItemExtraInfo?.imei2s,
                        deviceSns: orderItemExtraInfo?.deviceSns,
                        eanCodes: orderItemExtraInfo?.eanCodes,
                        orderStatus: order.status,
                        isOrderItemRefund: orderItem.isRefunding,
                    });
                });
            }

            return total;
        }, []);
    }, []);

    const columns: TableColumnsType<OrderItemRow> = [
        {
            title: "订单",
            dataIndex: "orderOriginalId",
            render(_, orderItemRow) {
                if (orderItemRow.index === 0) {
                    return <span>{orderItemRow.orderOriginalId}</span>;
                }

                return null;
            },
            onCell(orderItemRow) {
                return {
                    rowSpan: orderItemRow.index === 0 ? orderItemRow.orderItemLength : 0,
                    className: "vamiddle",
                };
            },
        },
        {
            title: "商品",
            render(_, orderItemRow) {
                return (
                    <div className="flex">
                        <div>
                            <ProxyImage width={60} src={orderItemRow.picture} />
                        </div>
                        <div className="ml10">
                            <p>{orderItemRow.title}</p>
                            <p className="text-gray small">{orderItemRow.skuProperties}</p>
                        </div>
                    </div>
                );
            },
        },
        {
            title: "商品编码(货号）",
            dataIndex: "skuExternalId",
        },
        {
            title: "数量",
            width: 120,
            render(_, { quantity, status, orderStatus, isOrderItemRefund }) {
                return (
                    <div>
                        <p>{quantity}</p>
                        {buildOrderItemStatus(orderStatus, status, isOrderItemRefund)}
                    </div>
                );
            },
        },
        {
            title: "商品串码(IMEI码)",
            dataIndex: "imeis",
            render(imeis, { originalId, orderOriginalId }) {
                return (
                    <Form.Item initialValue={imeis?.join(",")} name={[orderOriginalId, originalId, "imei"]}>
                        <Input placeholder="多个商品串码用，分开" />
                    </Form.Item>
                );
            },
        },
        showIMEI2ShopTypes.includes(shopType) && {
            title: "IMEI2码",
            dataIndex: "imei2s",
            render(imei2s, { originalId, orderOriginalId }) {
                return (
                    <Form.Item initialValue={imei2s?.join(",")} name={[orderOriginalId, originalId, "imei2"]}>
                        <Input placeholder="IMEI2码" />
                    </Form.Item>
                );
            },
        },
        showDeviceSnShopTypes.includes(shopType) && {
            title: "SN码",
            dataIndex: "deviceSns",
            render(deviceSns, { originalId, orderOriginalId }) {
                return (
                    <Form.Item initialValue={deviceSns?.join(",")} name={[orderOriginalId, originalId, "deviceSn"]}>
                        <Input placeholder="SN码" />
                    </Form.Item>
                );
            },
        },
        showEanCodeShopTypes.includes(shopType) && {
            title: "69码",
            dataIndex: "eanCodes",
            render(eanCodes, { originalId, orderOriginalId }) {
                return (
                    <Form.Item initialValue={eanCodes?.join(",")} name={[orderOriginalId, originalId, "eanCode"]}>
                        <Input placeholder="69码" />
                    </Form.Item>
                );
            },
        },
    ].filter(Boolean);

    return (
        <Form form={form}>
            <Table rowKey="id" dataSource={orderItemRows} pagination={false} columns={columns} />
        </Form>
    );
};

export default React.memo(IMEIFormTable);
